import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useOrganisation } from '../contexts/OrganisationContext';
import { Project, OrganisationMember } from '../types/api';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  FolderIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const OrganisationDashboardPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const {
    currentOrganisation,
    quotaUsage,
    currentUserRole,
    loading,
    selectOrganisation,
    refreshQuotaUsage,
  } = useOrganisation();

  const [projects, setProjects] = useState<Project[]>([]);
  const [members, setMembers] = useState<OrganisationMember[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (orgId && (!currentOrganisation || currentOrganisation.id !== orgId)) {
      selectOrganisation(orgId).catch((error) => {
        toast.error(t('dashboard.loadOrgError'));
        navigate('/organisations');
      });
    }
  }, [orgId, currentOrganisation, selectOrganisation, navigate]);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!currentOrganisation) return;

      try {
        setLoadingData(true);
        const [projectsData, membersData] = await Promise.all([
          apiService.getProjects(),
          apiService.getOrganisationMembers(currentOrganisation.id),
        ]);

        setProjects(projectsData);
        setMembers(membersData);
        await refreshQuotaUsage();
      } catch (error: unknown) {
        toast.error(t('dashboard.loadDataError'));
      } finally {
        setLoadingData(false);
      }
    };

    if (currentOrganisation) {
      loadDashboardData();
    }
  }, [currentOrganisation, refreshQuotaUsage]);

  const getQuotaPercentageColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-500/12 text-yellow-400';
      case 'admin':
        return 'bg-blue-500/12 text-blue-400';
      default:
        return 'bg-surface-3 text-text-secondary';
    }
  };

  if (loading || loadingData || !currentOrganisation) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-text-secondary">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      {/* Organization Header */}
      <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <BuildingOfficeIcon className="h-12 w-12 text-accent-400" />
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                {currentOrganisation.nom}
              </h1>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm text-text-tertiary">
                  {t('dashboard.plan')} {currentOrganisation.plan_type}
                </span>
                <span className="text-text-quaternary">•</span>
                <span className={`text-xs px-2 py-1 rounded-full ${getRoleBadgeColor(currentUserRole || '')}`}>
                  {currentUserRole?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/organisations/${currentOrganisation.id}/settings`}
              className="inline-flex items-center px-4 py-2 border border-border-1 rounded-md shadow-sm text-sm font-medium text-text-secondary bg-surface-3 hover:bg-surface-3"
            >
              <Cog6ToothIcon className="h-5 w-5 mr-2" />
              {t('dashboard.settings')}
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {/* Projects Count */}
        <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FolderIcon className="h-6 w-6 text-text-quaternary" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">
                    {t('dashboard.stats.projects')}
                  </dt>
                  <dd className="text-lg font-semibold text-text-primary">
                    {projects.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Members Count */}
        <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-text-quaternary" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">
                    {t('dashboard.stats.members')}
                  </dt>
                  <dd className="text-lg font-semibold text-text-primary">
                    {members.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* CPU Usage */}
        <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-text-quaternary" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">
                    {t('dashboard.stats.cpuUsed')}
                  </dt>
                  <dd className="text-lg font-semibold text-text-primary">
                    {quotaUsage?.cpu.percentage.toFixed(0)}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* RAM Usage */}
        <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowTrendingUpIcon className="h-6 w-6 text-text-quaternary" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">
                    {t('dashboard.stats.ramUsed')}
                  </dt>
                  <dd className="text-lg font-semibold text-text-primary">
                    {quotaUsage?.ram.percentage.toFixed(0)}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quota Usage Section */}
      {quotaUsage && (
        <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-text-primary">
              {t('dashboard.quotas.title')}
            </h2>
            <Link
              to={`/organisations/${currentOrganisation.id}/quotas`}
              className="text-sm font-medium text-accent-400 hover:text-accent-300"
            >
              {t('dashboard.quotas.viewDetails')} →
            </Link>
          </div>

          <div className="space-y-4">
            {/* CPU */}
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-text-secondary font-medium">{t('dashboard.quotas.cpu')}</span>
                <span className="text-text-tertiary">
                  {quotaUsage.cpu.used} / {quotaUsage.cpu.total} vCPU
                </span>
              </div>
              <div className="w-full bg-surface-3 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full ${getQuotaPercentageColor(quotaUsage.cpu.percentage)}`}
                  style={{ width: `${Math.min(quotaUsage.cpu.percentage, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* RAM */}
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-text-secondary font-medium">{t('dashboard.quotas.ram')}</span>
                <span className="text-text-tertiary">
                  {quotaUsage.ram.used} / {quotaUsage.ram.total} GB
                </span>
              </div>
              <div className="w-full bg-surface-3 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full ${getQuotaPercentageColor(quotaUsage.ram.percentage)}`}
                  style={{ width: `${Math.min(quotaUsage.ram.percentage, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Storage */}
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-text-secondary font-medium">{t('dashboard.quotas.storage')}</span>
                <span className="text-text-tertiary">
                  {quotaUsage.storage.used} / {quotaUsage.storage.total} GB
                </span>
              </div>
              <div className="w-full bg-surface-3 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full ${getQuotaPercentageColor(quotaUsage.storage.percentage)}`}
                  style={{ width: `${Math.min(quotaUsage.storage.percentage, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Projects & Members */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Projects */}
        <div className="bg-surface-2 shadow-card rounded-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-text-primary">
              {t('dashboard.recentProjects.title')}
            </h2>
            <Link
              to={`/organisations/${currentOrganisation.id}/projects`}
              className="text-sm font-medium text-accent-400 hover:text-accent-300"
            >
              {t('dashboard.recentProjects.viewAll')} →
            </Link>
          </div>

          {projects.length === 0 ? (
            <div className="text-center py-8">
              <FolderIcon className="mx-auto h-12 w-12 text-text-quaternary" />
              <p className="mt-2 text-sm text-text-tertiary">
                {t('dashboard.recentProjects.empty')}
              </p>
              <button
                onClick={() => navigate(`/organisations/${currentOrganisation.id}/projects`)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('dashboard.recentProjects.createProject')}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {projects.slice(0, 5).map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-3 bg-surface-3 rounded-card hover:bg-surface-3 cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <div className="flex items-center space-x-3">
                    <FolderIcon className="h-5 w-5 text-text-quaternary" />
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        {project.nom}
                      </p>
                      {project.description && (
                        <p className="text-xs text-text-tertiary">
                          {project.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-text-tertiary">
                    {new Date(project.date_creation).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Members */}
        <div className="bg-surface-2 shadow-card rounded-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-text-primary">
              {t('dashboard.orgMembers.title')}
            </h2>
            <Link
              to={`/organisations/${currentOrganisation.id}/members`}
              className="text-sm font-medium text-accent-400 hover:text-accent-300"
            >
              {t('dashboard.orgMembers.manage')} →
            </Link>
          </div>

          <div className="space-y-3">
            {members.slice(0, 5).map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 bg-surface-3 rounded-card"
              >
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    {member.prenom} {member.nom}
                  </p>
                  <p className="text-xs text-text-tertiary">{member.email}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getRoleBadgeColor(member.role_organisation)}`}>
                  {member.role_organisation.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 bg-surface-2 shadow-card rounded-card p-6">
        <h2 className="text-lg font-medium text-text-primary mb-4">
          {t('dashboard.quickActions.title')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to={`/organisations/${currentOrganisation.id}/projects`}
            className="flex items-center justify-center p-4 border-2 border-dashed border-border-1 rounded-card hover:border-accent-500 transition-colors"
          >
            <div className="text-center">
              <PlusIcon className="mx-auto h-8 w-8 text-text-quaternary" />
              <span className="mt-2 block text-sm font-medium text-text-primary">
                {t('dashboard.quickActions.newProject')}
              </span>
            </div>
          </Link>

          <Link
            to={`/organisations/${currentOrganisation.id}/members`}
            className="flex items-center justify-center p-4 border-2 border-dashed border-border-1 rounded-card hover:border-accent-500 transition-colors"
          >
            <div className="text-center">
              <UserGroupIcon className="mx-auto h-8 w-8 text-text-quaternary" />
              <span className="mt-2 block text-sm font-medium text-text-primary">
                {t('dashboard.quickActions.inviteMember')}
              </span>
            </div>
          </Link>

          <Link
            to={`/organisations/${currentOrganisation.id}/plan`}
            className="flex items-center justify-center p-4 border-2 border-dashed border-border-1 rounded-card hover:border-accent-500 transition-colors"
          >
            <div className="text-center">
              <ArrowTrendingUpIcon className="mx-auto h-8 w-8 text-text-quaternary" />
              <span className="mt-2 block text-sm font-medium text-text-primary">
                {t('dashboard.quickActions.managePlan')}
              </span>
            </div>
          </Link>

          <Link
            to={`/organisations/${currentOrganisation.id}/settings`}
            className="flex items-center justify-center p-4 border-2 border-dashed border-border-1 rounded-card hover:border-accent-500 transition-colors"
          >
            <div className="text-center">
              <Cog6ToothIcon className="mx-auto h-8 w-8 text-text-quaternary" />
              <span className="mt-2 block text-sm font-medium text-text-primary">
                {t('dashboard.quickActions.settings')}
              </span>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrganisationDashboardPage;
